import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { colors } from "..";
import { IResponsePositionGraphic } from "../../../../../interfaces/OrderPostions";
import { Flex } from "@mantine/core";
import { useTranslation } from "react-i18next";

interface IProps {
  data: IResponsePositionGraphic;
}

// Função utilitária para arredondar para múltiplos de 0.5
function roundDownToHalf(num: number) {
  return Math.floor(num * 2) / 2;
}
function roundUpToHalf(num: number) {
  return Math.ceil(num * 2) / 2;
}

export function Graphic({ data }: IProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [series, setSeries] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [labels, setLabels] = useState<string[][]>([]);

  useEffect(() => {
    const currencies = Array.from(
      new Set(data.total_by_currency.map((item) => item.currency))
    );

    const newSeries = data.items.map((item, index) => ({
      name: `${item.product_title}-${item.symbol}`,
      data: currencies.map((currency) =>
        currency === item.currency ? item.profit_loss : 0
      ),
    }));

    const newLabels = data.items.map((item) => {
      return currencies.map((currency) => {
        const value = currency === item.currency ? item.profit_loss : 0;

        const formatted = new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: currency || "USD",
          minimumFractionDigits: 2,
        }).format(value);

        return value < 0 ? `-${formatted.replace("-", "")}` : formatted;
      });
    });

    setSeries(newSeries);
    setCategories(currencies);
    setLabels(newLabels);
  }, [data]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: true, tools: { download: false } },
      },
      xaxis: {
        categories,
      },
      yaxis: {
        title: { text: t("operacoes.resumo_das_posicoes.value") },
        labels: { formatter: (value) => value.toFixed(2) },
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: "12px", colors: ["#fff"] },
        formatter: (value, { seriesIndex, dataPointIndex }) => {
          return labels?.[seriesIndex]?.[dataPointIndex] ?? "";
        },
      },
      tooltip: {
        y: {
          formatter: (value, { seriesIndex, dataPointIndex }) => {
            return labels?.[seriesIndex]?.[dataPointIndex] ?? "";
          },
        },
      },
      plotOptions: {
        bar: {
          columnWidth: "80%",
          borderRadius: 4,
          dataLabels: { position: "center" },
        },
      },
      responsive: [
        { breakpoint: 450, options: { chart: { width: 200, height: 200 } } },
        { breakpoint: 650, options: { chart: { width: 320, height: 320 } } },
        { breakpoint: 991, options: { chart: { width: 450, height: 450 } } },
      ],
      colors: colors.color,
    }),
    [categories, labels, series, language]
  );

  return (
    <Flex w="100%" h="100%" direction="column" align="center" justify="center">
      <Chart
        options={options}
        series={series}
        type="bar"
        height={650}
        width={650}
      />
    </Flex>
  );
}
